import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/datepicker-custom.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Modal, Pagination } from "react-bootstrap";
import "../VirtualMeeting.css";
import { API_BASE_URL } from "../../utils/constants";
import { toast, ToastContainer } from "react-toastify";
import LoadingSpinner from "../../components/LoadingSpinner";
import AddDetailsModal from "../../components/AddDetailsModal";
import GlobalVideoUploadModal from "../../components/GlobalVideoUploadModal";
import VirtualMeetModalForTH from "./VirtualMeetModalForTH";
import { useHearingSession } from "../../hooks/useHearingSession";
import HearingLotSelect from "../../components/HearingLotSelect";

// ── Fetch cases for second hearing ────────────────────────────────────────────

const fetchSecondHearingCases = async (formattedDate, arbId) => {
  const response = await fetch(`${API_BASE_URL}/api/GetOppforNextHearing`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ Hearing_date: formattedDate, Arb_id: arbId, Meeting_No: 2 }),
  });
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  const result = await response.json();
  const parsed = Array.isArray(result) ? result : JSON.parse(result);
  return parsed.map((item) => ({
    Case_id: item.caseId,
    Reference_No: item.referenceNo,
    Second_Hearing_date: item.secondHearingDate
      ? new Date(item.secondHearingDate).toLocaleDateString("en-US")
      : "",
    Cust_name: item.Cust_name,
    detailsSaved: item.detailsSaved ?? false,
    lotNo: item.lotNo,
    clientId: item.clientId,
    productId: item.productId,
  }));
};

// ─────────────────────────────────────────────────────────────────────────────

const SecondHearing = () => {
  const [showSecondModal, setShowSecondModal] = useState(false);

  const {
    arbId,
    selectedDate, formattedDate,
    availableDates, availableDateObjects, openToDate,
    data, filteredData, loading,
    currentItems, currentPage, setCurrentPage, totalPages, paginationPages,
    show, showVideoModal, setShowVideoModal,
    globalVideos, globalVideoInputRef, videosUploaded,
    lots, selectedLotKey, handleLotChange,
    handleDateChange, handleAppointments,
    handleAddDetails, handleClose, handleSaveDetails,
    handleGlobalVideoChange, handleRemoveGlobalVideo,
    handleCloseVideoModal, handleGlobalVideoUpload,
  } = useHearingSession({
    meetingNo: 2,
    availableDatesEndpoint: "AvailableSecondHearingDates",
    fetchCases: fetchSecondHearingCases,
  });

  if (loading) return <LoadingSpinner />;

  return (
    <div className="container mt-2">
      <h4>Virtual Meeting For Second Hearing</h4>

      <div className="row align-items-center my-2">
        <div className="col-md-2">
          <label htmlFor="datePicker" className="form-label">Select Date :-</label>
        </div>
        <div className="col-md-2">
          <DatePicker
            id="datePicker"
            selected={selectedDate}
            onChange={handleDateChange}
            includeDates={availableDateObjects}
            openToDate={openToDate}
            placeholderText="Select a date"
            dateFormat="MM/dd/yyyy"
            className="form-control date-picker-sm"
            disabled={availableDates.length === 0}
          />
        </div>
        {availableDates.length === 0 && (
          <div className="col-md-4">
            <small className="text-muted">No second hearing dates found</small>
          </div>
        )}
        {availableDates.length > 0 && (
          <div className="col-md-2">
            <button className="custBtn" onClick={handleAppointments}>Appointments</button>
          </div>
        )}
        <HearingLotSelect lots={lots} selectedLotKey={selectedLotKey} onChange={handleLotChange} />
        <div className="col-md-3">
          {filteredData.length > 0 && !videosUploaded && (
            <button className="custBtn" onClick={() => setShowVideoModal(true)}>
              Upload Videos
            </button>
          )}
          {filteredData.length > 0 && videosUploaded && (
            <button
              className="custBtn"
              onClick={() => setShowSecondModal(true)}
              style={{ whiteSpace: "nowrap" }}
            >
              Assign Third Hearing
            </button>
          )}
        </div>
      </div>

      {/* Cases Table */}
      <div className="row">
        <div className="col-md-12">
          <div className="table-responsive">
            {currentItems.length > 0 && (
              <table
                className="table table-striped table-bordered table-hover table-sm mt-3 text-center"
                style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
              >
                <thead>
                  <tr>
                    {videosUploaded && <th>Action</th>}
                    <th>Case ID</th>
                    <th>Reference No</th>
                    <th>Customer Name</th>
                    <th>Hearing Date</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, index) => (
                    <tr key={index}>
                      {videosUploaded && (
                        <td>
                          {item.detailsSaved
                            ? <span className="text-success fw-semibold">✓ Saved</span>
                            : <button className="custBtn" onClick={() => handleAddDetails(item.Case_id)}>Assign to Contested</button>
                          }
                        </td>
                      )}
                      <td>{item.Case_id}</td>
                      <td>{item.Reference_No}</td>
                      <td>{item.Cust_name}</td>
                      <td>{item.Second_Hearing_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {totalPages > 1 && (
            <Pagination className="justify-content-center">
              <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} />
              {paginationPages.map(({ n, active }) => (
                <Pagination.Item key={n} active={active} onClick={() => setCurrentPage(n)}>{n}</Pagination.Item>
              ))}
              <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} />
            </Pagination>
          )}
        </div>
      </div>

      {/* ── Add Details Modal ──────────────────────────────────────────────────── */}
      <AddDetailsModal show={show} onHide={handleClose} onSave={handleSaveDetails} />

      {/* ── Upload Videos Modal ───────────────────────────────────────────────── */}
      <GlobalVideoUploadModal
        show={showVideoModal}
        onHide={handleCloseVideoModal}
        videoInputRef={globalVideoInputRef}
        videos={globalVideos}
        onVideoAdd={handleGlobalVideoChange}
        onVideoRemove={handleRemoveGlobalVideo}
        onUpload={handleGlobalVideoUpload}
        formattedDate={formattedDate}
        hearingLabel="second"
      />

      {/* ── Assign Third Hearing Modal ─────────────────────────────────────────── */}
      <Modal show={showSecondModal} onHide={() => setShowSecondModal(false)} className="modal-xl">
        <Modal.Header className="customModal" style={{ position: "relative" }}>
          <Modal.Title>Assign Second Hearing Date</Modal.Title>
          <Button
            style={{ position: "absolute", right: "15px", color: "orange", backgroundColor: "transparent", border: "none", fontSize: "40px" }}
            onClick={() => setShowSecondModal(false)}
          >
            &times;
          </Button>
        </Modal.Header>
        <Modal.Body>
          <VirtualMeetModalForTH
            appointments={data}
            arbId={arbId}
            setShowSecondModal={setShowSecondModal}
            meetingNo={2}
            onSuccess={async () => {
              setShowSecondModal(false);
              toast.success("Third hearing assigned successfully!", { theme: "colored", autoClose: 2000 });
              await handleAppointments();
            }}
          />
        </Modal.Body>
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default SecondHearing;
